import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import {
  useCafeStatus,
  useTomorrowOptions,
  useSubmitVote,
  useSuggestions,
} from '../../hooks/useCafe';
import CafeSuggestionsModal from './CafeSuggestionsModal';

export default function CafeWidget() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [isOpen, setIsOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  // Distance from viewport bottom; lifts above any visible
  // [data-app-bottom-bar] (e.g. shipments / finance bulk actions bars).
  const [bottomOffsetPx, setBottomOffsetPx] = useState(16);

  const rootRef = useRef<HTMLDivElement>(null);

  const { data: status, isLoading: statusLoading } = useCafeStatus();
  const { data: tomorrow, isLoading: tomorrowLoading } = useTomorrowOptions();
  const { data: suggestionsData } = useSuggestions();
  const submitVote = useSubmitVote();

  useEffect(() => {
    if (tomorrow?.user_vote) {
      setSelectedOption(tomorrow.user_vote);
    }
  }, [tomorrow?.user_vote]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const node = rootRef.current;
      if (node && !node.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen]);

  // Avoid overlapping any fixed bottom bar (BulkActionsBar / FinanceBulkActionsBar).
  // Watches the DOM for `[data-app-bottom-bar]` and tracks each one's height
  // so we always sit `8px` above the tallest visible bar.
  useEffect(() => {
    const GAP_PX = 8;
    const BASE_PX = 16;

    const elementResizeObservers = new Map<Element, ResizeObserver>();

    const recompute = () => {
      const bars = Array.from(document.querySelectorAll<HTMLElement>('[data-app-bottom-bar]'));
      if (bars.length === 0) {
        setBottomOffsetPx(BASE_PX);
        return;
      }
      const tallest = bars.reduce((max, el) => Math.max(max, el.getBoundingClientRect().height), 0);
      setBottomOffsetPx(Math.round(tallest + GAP_PX));
    };

    const observeBars = () => {
      const bars = document.querySelectorAll<HTMLElement>('[data-app-bottom-bar]');
      for (const el of Array.from(elementResizeObservers.keys())) {
        if (!document.body.contains(el)) {
          elementResizeObservers.get(el)?.disconnect();
          elementResizeObservers.delete(el);
        }
      }
      bars.forEach(bar => {
        if (elementResizeObservers.has(bar)) return;
        const ro = new ResizeObserver(recompute);
        ro.observe(bar);
        elementResizeObservers.set(bar, ro);
      });
    };

    recompute();
    observeBars();

    const mutationObserver = new MutationObserver(() => {
      observeBars();
      recompute();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', recompute);

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener('resize', recompute);
      elementResizeObservers.forEach(ro => ro.disconnect());
      elementResizeObservers.clear();
    };
  }, []);

  const handleVote = async (optionId: string) => {
    setSelectedOption(optionId);
    try {
      await submitVote.mutateAsync(optionId);
    } catch (error) {
      console.error('Failed to submit vote:', error);
      setSelectedOption(tomorrow?.user_vote || null);
    }
  };

  const formatTimeRemaining = (time: { hours: number; minutes: number } | null) => {
    if (!time) return '';
    if (time.hours > 0) {
      return `${time.hours}h ${time.minutes}m`;
    }
    return `${time.minutes}m`;
  };

  if (statusLoading || !status) {
    return null;
  }

  const todayMenu = status.today_menu;
  const hasVoted = tomorrow?.has_voted || false;
  const votingClosed = status.voting_closed;

  const triggerLabel = todayMenu?.dish_name || t('cafe.noMenu', 'No menu');

  return (
    <>
      <div
        ref={rootRef}
        className="fixed left-4 z-50 flex flex-col items-start gap-0 transition-[bottom] duration-200 ease-out"
        style={{ bottom: `${bottomOffsetPx}px` }}
      >
        {isOpen && (
          <div
            className="mb-2 w-[min(17rem,calc(100vw-2rem))] max-h-[min(70vh,24rem)] overflow-y-auto rounded-xl border border-line bg-white p-3 shadow-pop"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t('cafe.title', 'Cafeteria')}
            </p>

            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-2.5">
                <div className="mb-1.5 flex items-center gap-1.5 text-emerald-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0" />
                  <span className="text-xs font-semibold">
                    {t('cafe.todayLunch', "Today's Lunch")}
                  </span>
                </div>
                {todayMenu ? (
                  <div>
                    <p className="text-sm font-bold leading-snug text-emerald-900">
                      {todayMenu.dish_name}
                    </p>
                    {todayMenu.dish_name_ar && (
                      <p className="mt-0.5 text-xs text-emerald-700">{todayMenu.dish_name_ar}</p>
                    )}
                    {todayMenu.was_tie && (
                      <p className="mt-1 text-[11px] text-emerald-600">
                        {t('cafe.decidedByChef', 'Decided by chef')}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs italic text-emerald-600">
                    {t('cafe.noMenuToday', 'No menu set for today')}
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-brand-100 bg-brand-50 p-2.5">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5 text-brand-700">
                    <SparklesIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate text-xs font-semibold">
                      {t('cafe.voteForTomorrow', 'Vote for Tomorrow')}
                    </span>
                  </div>
                  {status.time_remaining && (
                    <div className="flex shrink-0 items-center gap-0.5 text-[11px] text-brand-600">
                      <ClockIcon className="h-3.5 w-3.5" />
                      <span>{formatTimeRemaining(status.time_remaining)}</span>
                    </div>
                  )}
                </div>

                {tomorrowLoading ? (
                  <div className="py-3 text-center">
                    <div className="mx-auto h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-b-transparent" />
                  </div>
                ) : !status.has_tomorrow_options ? (
                  <p className="py-1 text-center text-xs italic text-brand-600">
                    {t('cafe.chefPreparing', 'Chef is preparing options...')}
                  </p>
                ) : status.has_tie ? (
                  <p className="py-1 text-center text-xs italic text-amber-600">
                    {t('cafe.tieWaiting', "It's a tie! Waiting for chef...")}
                  </p>
                ) : votingClosed && status.result_finalized ? (
                  <div className="space-y-1.5">
                    {tomorrow?.options.map(option => (
                      <div
                        key={option.id}
                        className={`rounded-md border p-1.5 text-xs ${
                          tomorrow.options.reduce((max, o) =>
                            (o.vote_count || 0) > (max.vote_count || 0) ? o : max
                          ).id === option.id
                            ? 'border-brand-200 bg-brand-100'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{option.dish_name}</span>
                          <span className="shrink-0 font-bold text-brand-700">
                            {option.vote_count || 0} {t('cafe.votes', 'votes')}
                          </span>
                        </div>
                      </div>
                    ))}
                    <p className="mt-1 text-center text-[11px] text-brand-600">
                      🎉 {t('cafe.votingEnded', 'Voting has ended!')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {tomorrow?.options.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleVote(option.id)}
                        disabled={votingClosed || submitVote.isPending}
                        className={`w-full rounded-md border p-1.5 text-start text-xs transition-all ${
                          selectedOption === option.id
                            ? 'border-brand-600 bg-brand-600 text-white shadow-sm'
                            : 'border-gray-200 bg-white hover:border-brand-300 hover:bg-brand-50'
                        } ${votingClosed ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 ${
                              selectedOption === option.id
                                ? 'border-white bg-white'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedOption === option.id && (
                              <div className="h-1.5 w-1.5 rounded-full bg-brand-600" />
                            )}
                          </div>
                          <span className="font-medium">{option.dish_name}</span>
                        </div>
                        {option.dish_name_ar && (
                          <p
                            className={`mt-0.5 ps-5 text-[11px] ${selectedOption === option.id ? 'text-brand-100' : 'text-gray-500'} ${
                              isRtl ? 'text-right' : 'text-left'
                            }`}
                          >
                            {option.dish_name_ar}
                          </p>
                        )}
                      </button>
                    ))}

                    {hasVoted && (
                      <p className="flex items-center justify-center gap-1 text-[11px] text-brand-600">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        {t('cafe.votedSuccessfully', 'Vote recorded!')}
                      </p>
                    )}

                    {votingClosed && (
                      <p className="text-center text-[11px] text-gray-500">
                        {t('cafe.votingClosed', 'Voting is closed')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {suggestionsData?.suggestions_open && (
                <button
                  type="button"
                  onClick={() => setShowSuggestions(true)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 py-1.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
                >
                  <LightBulbIcon className="h-4 w-4" />
                  {t('cafe.suggestDish', 'Suggest a dish')}
                </button>
              )}

              {tomorrow && tomorrow.total_voters > 0 && (
                <p className="text-center text-[10px] text-gray-400">
                  {tomorrow.total_voters} {t('cafe.peopleVoted', 'people have voted')}
                </p>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setIsOpen(o => !o)}
          className="flex max-w-[min(17rem,calc(100vw-2rem))] items-center gap-2 rounded-full border border-amber-500/80 bg-amber-600 px-3 py-2 text-xs font-semibold text-white shadow-pop transition hover:bg-amber-700"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <span className="text-lg leading-none" aria-hidden>
            🍽️
          </span>
          <span className="min-w-0 flex-1 truncate text-start">{triggerLabel}</span>
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-90" />
          ) : (
            <ChevronUpIcon className="h-4 w-4 shrink-0 opacity-90" />
          )}
        </button>
      </div>

      <CafeSuggestionsModal isOpen={showSuggestions} onClose={() => setShowSuggestions(false)} />
    </>
  );
}
